import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const GeneralSettingsTab = () => {
  const [settings, setSettings] = useState({
    organizationName: "TechCorp Industries",
    timezone: "America/New_York",
    dateFormat: "MM/DD/YYYY",
    currency: "INR",
    language: "en",
    sessionTimeout: "30",
    enableMFA: true,
    enableSSO: true,
    enableAuditLog: true,
    maintenanceMode: false
  });

  const [isModified, setIsModified] = useState(false);

  const timezoneOptions = [
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "UTC", label: "Coordinated Universal Time (UTC)" }
  ];

  const dateFormatOptions = [
    { value: "MM/DD/YYYY", label: "MM/DD/YYYY (US Format)" },
    { value: "DD/MM/YYYY", label: "DD/MM/YYYY (European Format)" },
    { value: "YYYY-MM-DD", label: "YYYY-MM-DD (ISO Format)" }
  ];

  const currencyOptions = [
    { value: "INR", label: "Indian Rupee (₹)" },
    { value: "USD", label: "US Dollar ($)" },
    { value: "EUR", label: "Euro (€)" },
    { value: "GBP", label: "British Pound (£)" },
    { value: "CAD", label: "Canadian Dollar (C$)" }
  ];

  const languageOptions = [
    { value: "en", label: "English" },
    { value: "es", label: "Spanish" },
    { value: "fr", label: "French" },
    { value: "de", label: "German" }
  ];

  const sessionTimeoutOptions = [
    { value: "15", label: "15 minutes" },
    { value: "30", label: "30 minutes" },
    { value: "60", label: "1 hour" },
    { value: "120", label: "2 hours" },
    { value: "480", label: "8 hours" }
  ];

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setIsModified(true);
  };

  const handleSave = () => {
    console.log('Saving general settings:', settings);
    setIsModified(false);
  };

  const handleReset = () => {
    setSettings({
      organizationName: "TechCorp Industries",
      timezone: "America/New_York",
      dateFormat: "MM/DD/YYYY",
      currency: "INR",
      language: "en",
      sessionTimeout: "30",
      enableMFA: true,
      enableSSO: true,
      enableAuditLog: true,
      maintenanceMode: false
    });
    setIsModified(false);
  };

  return (
    <div className="space-y-8">
      {/* Organization Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Building" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">Organization Settings</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Organization Name"
            type="text"
            value={settings?.organizationName}
            onChange={(e) => handleInputChange('organizationName', e?.target?.value)}
            placeholder="Enter organization name"
            required
          />
          
          <Select
            label="Default Timezone"
            options={timezoneOptions}
            value={settings?.timezone}
            onChange={(value) => handleInputChange('timezone', value)}
          />
          
          <Select
            label="Date Format"
            options={dateFormatOptions}
            value={settings?.dateFormat}
            onChange={(value) => handleInputChange('dateFormat', value)}
          />
          
          <Select
            label="Default Currency"
            options={currencyOptions}
            value={settings?.currency}
            onChange={(value) => handleInputChange('currency', value)}
          />
          
          <Select
            label="System Language"
            options={languageOptions}
            value={settings?.language}
            onChange={(value) => handleInputChange('language', value)}
          />
          
          <Select
            label="Session Timeout"
            description="Automatic logout after inactivity"
            options={sessionTimeoutOptions}
            value={settings?.sessionTimeout}
            onChange={(value) => handleInputChange('sessionTimeout', value)}
          />
        </div>
      </div>
      {/* Security Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Shield" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">Security Settings</h3>
        </div>
        
        <div className="space-y-4">
          <Checkbox
            label="Enable Multi-Factor Authentication (MFA)"
            description="Require additional verification for user logins"
            checked={settings?.enableMFA}
            onChange={(e) => handleInputChange('enableMFA', e?.target?.checked)}
          />
          
          <Checkbox
            label="Enable Single Sign-On (SSO)"
            description="Allow users to authenticate via Active Directory/LDAP"
            checked={settings?.enableSSO}
            onChange={(e) => handleInputChange('enableSSO', e?.target?.checked)}
          />
          
          <Checkbox
            label="Enable Audit Logging"
            description="Track all user actions and system changes"
            checked={settings?.enableAuditLog}
            onChange={(e) => handleInputChange('enableAuditLog', e?.target?.checked)}
          />
        </div>
      </div>
      {/* System Maintenance */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Settings" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">System Maintenance</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-warning" />
              <div>
                <p className="font-medium text-card-foreground">Maintenance Mode</p>
                <p className="text-sm text-muted-foreground">Temporarily disable user access for system updates</p>
              </div>
            </div>
            <Checkbox
              checked={settings?.maintenanceMode}
              onChange={(e) => handleInputChange('maintenanceMode', e?.target?.checked)}
            />
          </div>
          
          {settings?.maintenanceMode && (
            <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <p className="font-medium text-error">Warning: Maintenance Mode Active</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Users will be unable to access the system. Only administrators can log in during maintenance mode.
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Clock" size={16} />
          <span>Last updated: Aug 19, 2025 at 4:36 AM</span>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!isModified}
          >
            Reset Changes
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={!isModified}
            iconName="Save"
            iconPosition="left"
          >
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettingsTab;